import { useCallback, useEffect, useState, useMemo } from 'react';

import {
  PeoplePageFetchState,
  PeoplePageResponse,
} from '@components/people_tags/customPeopleTagTypes';

import { usePeopleManagementContext } from '../PeopleManagementContext';

const mockData = {
  data: [
    {
      face_label: null,
      gcs_path:
        'https://cdn.bfldr.com/55NKS02W/at/jjs8wgxprqr6k36f5wg46r89/dog8.jpeg?auto=webp&format=png',
      person_id: 'c53ce846-af94-4942-a7d1-33475d06519c',
    },
    {
      face_label: null,
      gcs_path:
        'https://cdn.bfldr.com/55NKS02W/at/2jxnn6559gsnwqmwz4zrw/dog9.jpeg?auto=webp&format=png',
      person_id: 'd7e627fe-9a54-4697-88ab-4e2c5f223ec0',
    },
    {
      face_label: null,
      gcs_path:
        'https://cdn.bfldr.com/55NKS02W/at/5wfbgrcnrmgwqmxrnq7mrv3w/dog7.jpeg?auto=webp&format=png',
      person_id: '01aebc13-0412-4ac2-9c64-069e0193a1be',
    },
  ],
  meta: {
    bf_processed: true,
    current_page: 1,
    next_page: 1,
    prev_page: null,
    total_count: 3,
    total_pages: 1,
  },
};

export const useFetchPeopleHubData = (): PeoplePageFetchState => {
  const { state } = usePeopleManagementContext();
  const [rawPeopleData, setRawPeopleData] = useState<PeoplePageResponse>(undefined);
  const [peopleDataLoading, setPeopleDataLoading] = useState<boolean>(true);
  const [initialLoadingState, setInitialLoadingState] = useState<boolean>(true);
  const [peopleDataError, setPeopleDataError] = useState<boolean>(false);

  const loadMockData = useCallback(async () => {
    try {
      // Only clear the data on initial load or filter/sort changes, not during search
      if (!state.search || state.search === '') {
        setRawPeopleData(undefined);
      }
      setPeopleDataLoading(true);
      console.log('Loading data with search term:', state.search); // Debug log
      // Artificial delay to mimic network latency
      await new Promise((res) => setTimeout(res, 500));
      setRawPeopleData(mockData);
      setPeopleDataError(false);
    } catch {
      // Only clear on error if not during search
      if (!state.search || state.search === '') {
        setRawPeopleData(undefined);
      }
      setPeopleDataError(true);
    } finally {
      setPeopleDataLoading(false);
      setInitialLoadingState(false);
    }
  }, [state.search, state.filter, state.sort]); // Include state dependencies

  useEffect(() => {
    loadMockData();
  }, [loadMockData, state.search, state.filter, state.sort]);

  // Apply filtering and sorting based on context state
  const filteredPeopleData = useMemo(() => {
    if (!rawPeopleData?.data) {
      return rawPeopleData;
    }

    let filtered = [...rawPeopleData.data];

    // Apply search filter
    if (state.search.trim()) {
      const searchTerm = state.search.toLowerCase();
      filtered = filtered.filter((person) => {
        const name = person.face_label || 'unnamed';
        return name.toLowerCase().includes(searchTerm);
      });
    }

    // Apply category filter
    switch (state.filter) {
      case 'named':
        filtered = filtered.filter((person) => person.face_label !== null);
        break;
      case 'unnamed':
        filtered = filtered.filter((person) => person.face_label === null);
        break;
      case 'all':
      default:
        // No additional filtering needed
        break;
    }

    // Apply sorting
    switch (state.sort) {
      case 'name_asc':
        filtered.sort((a, b) => {
          const nameA = a.face_label || 'unnamed';
          const nameB = b.face_label || 'unnamed';
          return nameA.localeCompare(nameB);
        });
        break;
      case 'name_desc':
        filtered.sort((a, b) => {
          const nameA = a.face_label || 'unnamed';
          const nameB = b.face_label || 'unnamed';
          return nameB.localeCompare(nameA);
        });
        break;
      case 'recent':
      default:
        // Keep original order for "most recent"
        break;
    }

    return {
      ...rawPeopleData,
      data: filtered,
      meta: {
        ...rawPeopleData.meta,
        total_count: filtered.length,
      },
    };
  }, [rawPeopleData, state.search, state.filter, state.sort]);

  return {
    initialLoadingState,
    peopleData: filteredPeopleData,
    peopleDataError,
    peopleDataLoading,
    refetchPeopleData: loadMockData,
  };
};
